import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarPlus,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  Phone,
  Trash2,
  UserPlus,
} from 'lucide-react';
import type {
  Lead,
  LeadInput,
  LeadStatus,
  PaginatedResponse,
  ProposalAttachment,
} from '@oneup/shared';
import { LCC_LEAD_STATUS_LABELS } from '@oneup/shared';
import { leadsApi } from '@/lib/api';
import { DEMO_MODE } from '@/platform/demo-mode';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FormDrawer } from '@/components/form-drawer';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DetailSectionHeading,
  DetailsActionBar,
  DetailsDrawer,
  DetailsDrawerBody,
  DetailsDrawerError,
  DetailsDrawerSkeleton,
  PlaceholderActionButton,
} from '@/components/details-drawer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MarkdownNotes } from '@/components/markdown-notes';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/ui';
import {
  ManagementCardGrid,
  ManagementFooterSummary,
  ManagementHeader,
  ManagementLoadingState,
  ManagementPage,
  ManagementTable,
  type ManagementActiveFilter,
} from '@/components/management';
import { MANAGEMENT_PAGES, usePersistentManagementView } from '@/platform/management-pages';

const DEFAULT_LEAD_STATUSES: readonly LeadStatus[] = [
  'New Lead',
  'Spotted Opportunity',
  'In Progress',
  'Follow Up Later',
  'Signed!',
  'Archived',
];

/**
 * In the Lead Command Center demo the pipeline is the Supabase-native LCC
 * one (New → … → Converted); everywhere else the classic list applies.
 * Status values travel as display labels either way.
 */
const LEAD_STATUSES: readonly string[] =
  DEMO_MODE === 'lead-command-center' ? LCC_LEAD_STATUS_LABELS : DEFAULT_LEAD_STATUSES;

const DEFAULT_LEAD_STATUS: string = LEAD_STATUSES[0] ?? 'New Lead';

type LeadStatusFilter = string;

const LIST_KEY = ['leads', 'all'] as const;
const DETAIL_KEY = (id: string) => ['leads', 'detail', id] as const;

export function LeadsView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { leadId } = useParams<{ leadId: string }>();
  const activeStatus = parseStatus(searchParams.get('status'));
  const search = searchParams.get('search') ?? '';
  const [viewMode, setViewMode] = usePersistentManagementView(
    'leads',
    searchParams,
    setSearchParams,
  );
  const setNotice = useUiStore((state) => state.setNotice);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => leadsApi.list({ limit: 500 }),
  });

  const leads = data?.items ?? [];
  const filtered = useMemo(() => {
    const lowerSearch = search.trim().toLowerCase();
    return leads
      .filter((lead) => !activeStatus || lead.status === activeStatus)
      .filter((lead) => !lowerSearch || matchesSearch(lead, lowerSearch))
      .sort(compareLeads);
  }, [activeStatus, leads, search]);

  const setStatus = useCallback(
    (status: LeadStatusFilter) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (params.get('status') === status) params.delete('status');
        else params.set('status', status);
        return params;
      });
    },
    [setSearchParams],
  );

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value) params.set('search', value);
          else params.delete('search');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const openLead = useCallback(
    (id: string, edit = false) => {
      const params = new URLSearchParams(searchParams);
      if (edit) params.set('edit', '1');
      else params.delete('edit');
      const qs = params.toString();
      navigate(qs ? `/pipeline/leads/${id}?${qs}` : `/pipeline/leads/${id}`);
    },
    [navigate, searchParams],
  );

  if (isLoading) {
    return <ManagementLoadingState metricCount={6} />;
  }

  if (error) {
    return (
      <Card className="p-6">
        <h2 className="mb-1 text-base font-semibold text-destructive">Could not load leads</h2>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </Card>
    );
  }

  const stats = computeStats(leads);
  const page = MANAGEMENT_PAGES.leads;
  const activeFilters: ManagementActiveFilter[] = [];
  if (activeStatus) {
    activeFilters.push({
      id: 'status',
      label: 'Status',
      value: activeStatus,
      onClear: () => setStatus(activeStatus),
    });
  }
  if (search) {
    activeFilters.push({
      id: 'search',
      label: 'Search',
      value: `"${search}"`,
      onClear: () => setSearch(''),
    });
  }

  return (
    <ManagementPage>
      <ManagementHeader
        title={page.title}
        description={page.description}
        source={page.source}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Search leads, contacts, notes',
        }}
        filters={[
          {
            id: 'lead-status',
            label: 'Status',
            value: activeStatus,
            onChange: (value) => setStatus(value as LeadStatusFilter),
            options: LEAD_STATUSES.map((status) => ({
              value: status,
              label: status,
              count: stats[status] ?? 0,
            })),
          },
        ]}
        activeFilters={activeFilters}
        primaryAction={{ label: 'Create Lead', icon: UserPlus, onClick: () => setCreateOpen(true) }}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {viewMode === 'table' ? (
        <ManagementTable>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-[11px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
                  <th className="px-3 py-2.5">Lead / Project Name</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Business Name</th>
                  <th className="px-3 py-2.5">Contact</th>
                  <th className="px-3 py-2.5">Email / Phone</th>
                  <th className="px-3 py-2.5">Business Website</th>
                  <th className="px-3 py-2.5">Organization</th>
                  <th className="px-3 py-2.5">Last Modified</th>
                  <th className="px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-3 py-12 text-center text-sm text-muted-foreground"
                    >
                      No leads match this view.
                    </td>
                  </tr>
                )}
                {filtered.map((lead) => (
                  <tr
                    key={lead.id}
                    onClick={() => openLead(lead.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openLead(lead.id);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    className={cn(
                      'cursor-pointer border-t border-border transition-colors hover:bg-muted/30',
                      'focus-visible:bg-muted/40 focus-visible:outline-none',
                      leadId === lead.id && 'bg-primary/5',
                    )}
                  >
                    <td className="min-w-[260px] px-3 py-2.5">
                      <div className="font-medium text-foreground">{lead.projectName}</div>
                      {lead.proposalSummary && (
                        <div className="mt-1 line-clamp-2 text-[12px] text-muted-foreground">
                          {lead.proposalSummary}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5">{renderStatus(lead.status)}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {emptyDash(lead.businessName)}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {emptyDash(lead.fullName)}
                      {lead.businessRelationTitle && (
                        <div className="text-[11px]">{lead.businessRelationTitle}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      <ContactLinks lead={lead} stacked />
                    </td>
                    <td className="px-3 py-2.5">
                      <WebsiteLink url={lead.businessWebsite} />
                    </td>
                    <td className="px-3 py-2.5">
                      <ChipList values={lead.organizationIds} />
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {formatDateTime(lead.lastModifiedAt)}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex min-w-[210px] flex-wrap gap-1.5">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={(event) => {
                            event.stopPropagation();
                            openLead(lead.id);
                          }}
                        >
                          Review/Open
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={(event) => {
                            event.stopPropagation();
                            openLead(lead.id, true);
                          }}
                        >
                          Edit
                        </Button>
                        <PlaceholderActionButton
                          onClick={(event) => {
                            event.stopPropagation();
                            setNotice(`Create Proposal is not wired yet for ${lead.projectName}`);
                          }}
                        >
                          Create Proposal
                        </PlaceholderActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ManagementTable>
      ) : (
        <LeadCardView
          leads={filtered}
          activeLeadId={leadId}
          onOpen={openLead}
          onCreateProposal={(lead) =>
            setNotice(`Create Proposal is not wired yet for ${lead.projectName}`)
          }
        />
      )}

      <ManagementFooterSummary>
        Showing <span className="tabular-nums text-foreground">{filtered.length}</span>
        {leads.length !== filtered.length && (
          <>
            {' '}
            of <span className="tabular-nums">{leads.length}</span> loaded
          </>
        )}
      </ManagementFooterSummary>

      <CreateLeadDrawer open={createOpen} onOpenChange={setCreateOpen} />
      {leadId && <LeadDrawer leadId={leadId} initialEditing={searchParams.get('edit') === '1'} />}
    </ManagementPage>
  );
}

function LeadCardView({
  leads,
  activeLeadId,
  onOpen,
  onCreateProposal,
}: {
  leads: Lead[];
  activeLeadId: string | undefined;
  onOpen: (id: string, edit?: boolean) => void;
  onCreateProposal: (lead: Lead) => void;
}) {
  if (leads.length === 0) {
    return (
      <Card className="px-3 py-8 text-center text-sm text-muted-foreground">
        No leads match this view.
      </Card>
    );
  }

  return (
    <ManagementCardGrid>
      {leads.map((lead) => (
        <Card
          key={lead.id}
          className={cn(
            'cursor-pointer space-y-3 p-3 transition-colors hover:bg-muted/25',
            activeLeadId === lead.id && 'bg-primary/5 ring-1 ring-primary/20',
          )}
          onClick={() => onOpen(lead.id)}
        >
          <div className="space-y-1">
            <div className="line-clamp-2 text-sm font-semibold text-foreground">
              {lead.projectName}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {renderStatus(lead.status)}
              {lead.businessName && <Badge variant="outline">{lead.businessName}</Badge>}
            </div>
          </div>
          {lead.proposalSummary && (
            <p className="line-clamp-2 text-[12px] text-muted-foreground">{lead.proposalSummary}</p>
          )}
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <LeadCardFact label="Contact" value={lead.fullName ?? '-'} />
            <LeadCardFact label="Modified" value={formatDateTime(lead.lastModifiedAt)} />
          </div>
          <div className="text-[12px] text-muted-foreground">
            <ContactLinks lead={lead} stacked />
          </div>
          <div className="flex flex-wrap gap-1.5" onClick={(event) => event.stopPropagation()}>
            <Button size="sm" variant="outline" onClick={() => onOpen(lead.id)}>
              Review/Open
            </Button>
            <Button size="sm" variant="outline" onClick={() => onOpen(lead.id, true)}>
              Edit
            </Button>
            <PlaceholderActionButton onClick={() => onCreateProposal(lead)}>
              Create Proposal
            </PlaceholderActionButton>
          </div>
        </Card>
      ))}
    </ManagementCardGrid>
  );
}

function LeadCardFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background px-2 py-1.5">
      <div className="text-[9px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 truncate text-[12px] font-medium text-foreground">{value}</div>
    </div>
  );
}

function CreateLeadDrawer({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const nameRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(() => emptyLeadDraft());

  useEffect(() => {
    if (!open) return;
    setDraft(emptyLeadDraft());
    const handle = window.setTimeout(() => nameRef.current?.focus(), 80);
    return () => window.clearTimeout(handle);
  }, [open]);

  const createMutation = useMutation({
    mutationFn: () => leadsApi.create(toLeadInput(draft) as LeadInput & { projectName: string }),
    onSuccess: (lead) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.setQueryData(DETAIL_KEY(lead.id), lead);
      onOpenChange(false);
      setNotice(`Created lead: ${lead.projectName}`);
      navigate(`/pipeline/leads/${lead.id}`);
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Could not create lead', 'destructive'),
  });

  const canSubmit = draft.projectName.trim().length > 0 && !createMutation.isPending;
  const submit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!canSubmit) return;
    createMutation.mutate();
  };

  return (
    <FormDrawer
      open={open}
      title="Create Lead"
      description="Creates a normalized Airtable Leads record. Computed fields are read-only after save."
      icon={UserPlus}
      widthClassName="sm:max-w-[680px] md:max-w-[760px]"
      submitLabel="Create Lead"
      pendingLabel="Creating..."
      canSubmit={canSubmit}
      pending={createMutation.isPending}
      onClose={() => onOpenChange(false)}
      onSubmit={submit}
      onReset={() => setDraft(emptyLeadDraft())}
      error={createMutation.isError ? <MutationError error={createMutation.error} /> : undefined}
    >
      <LeadEditForm draft={draft} setDraft={setDraft} nameRef={nameRef} />
    </FormDrawer>
  );
}

function LeadDrawer({ leadId, initialEditing }: { leadId: string; initialEditing: boolean }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: DETAIL_KEY(leadId),
    queryFn: () => leadsApi.get(leadId),
    initialData: () => {
      const list = queryClient.getQueryData<PaginatedResponse<Lead>>(LIST_KEY);
      return list?.items.find((lead) => lead.id === leadId);
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(LIST_KEY)?.dataUpdatedAt ?? 0,
    staleTime: 10_000,
  });

  const handleClose = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('edit');
    const qs = params.toString();
    navigate(qs ? `/pipeline/leads?${qs}` : '/pipeline/leads', { replace: true });
  };

  if (detailQuery.data) {
    return (
      <LeadDrawerBody
        lead={detailQuery.data}
        initialEditing={initialEditing}
        onClose={handleClose}
      />
    );
  }

  return (
    <DetailsDrawer
      title="Lead details"
      icon={UserPlus}
      onClose={handleClose}
      widthClassName="sm:max-w-[680px] md:max-w-[760px]"
    >
      {detailQuery.isError ? (
        <DetailsDrawerError error={detailQuery.error} fallback="Could not load lead." />
      ) : (
        <DetailsDrawerSkeleton />
      )}
    </DetailsDrawer>
  );
}

function LeadDrawerBody({
  lead,
  initialEditing,
  onClose,
}: {
  lead: Lead;
  initialEditing: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const [editing, setEditing] = useState(initialEditing);
  const [draft, setDraft] = useState(() => toLeadDraft(lead));

  useEffect(() => {
    setDraft(toLeadDraft(lead));
  }, [lead]);

  const patchMutation = useMutation({
    mutationFn: (patch: LeadInput) => leadsApi.update(lead.id, patch),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.setQueryData(DETAIL_KEY(updated.id), updated);
      setNotice('Lead updated');
      setEditing(false);
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Save failed', 'destructive'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => leadsApi.delete(lead.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.removeQueries({ queryKey: DETAIL_KEY(lead.id) });
      setNotice(`Deleted lead: ${lead.projectName}`);
      onClose();
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Delete failed', 'destructive'),
  });

  const save = () => patchMutation.mutate(toLeadInput(draft));
  const remove = () => {
    if (!window.confirm(`Delete lead "${lead.projectName}"? This removes the Airtable record.`))
      return;
    deleteMutation.mutate();
  };

  return (
    <DetailsDrawer
      title={lead.projectName}
      icon={UserPlus}
      onClose={onClose}
      widthClassName="sm:max-w-[680px] md:max-w-[760px]"
      description={
        <>
          {renderStatus(lead.status)}
          <span>{lead.businessName ?? lead.fullName ?? lead.id}</span>
          {patchMutation.isPending && (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              saving
            </span>
          )}
          {patchMutation.isError && (
            <span className="inline-flex items-center gap-1 text-destructive">
              <AlertCircle className="h-3 w-3" aria-hidden />
              save failed
            </span>
          )}
        </>
      }
    >
      <DetailsDrawerBody>
        <DetailsActionBar>
          <Button size="sm" variant="outline" onClick={() => setEditing((value) => !value)}>
            {editing ? 'Cancel' : 'Edit'}
          </Button>
          {editing && (
            <Button size="sm" onClick={save} disabled={patchMutation.isPending}>
              Save
            </Button>
          )}
          <PlaceholderActionButton onClick={() => setNotice('Create Proposal is not wired yet')}>
            Create Proposal
          </PlaceholderActionButton>
          <PlaceholderActionButton onClick={() => setNotice('Create Follow-Up is not wired yet')}>
            Create Follow-Up
          </PlaceholderActionButton>
          <WebsiteButton url={lead.businessWebsite} />
          <ContactActionButtons lead={lead} />
          <Button
            size="sm"
            variant="outline"
            onClick={remove}
            disabled={deleteMutation.isPending}
            className="text-destructive hover:text-destructive"
          >
            {deleteMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            )}
            Delete
          </Button>
        </DetailsActionBar>

        {editing ? (
          <Card className="space-y-3 p-3">
            <LeadEditForm draft={draft} setDraft={setDraft} />
          </Card>
        ) : (
          <LeadReadOnly lead={lead} />
        )}
      </DetailsDrawerBody>
    </DetailsDrawer>
  );
}

function LeadReadOnly({ lead }: { lead: Lead }) {
  return (
    <Tabs defaultValue="overview" className="space-y-3">
      <TabsList>
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="contact">Contact</TabsTrigger>
        <TabsTrigger value="notes">Opportunity Notes</TabsTrigger>
        <TabsTrigger value="relationships">Relationships</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-3">
        <Card className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2">
          <Fact label="Project Name" value={lead.projectName} />
          <Fact label="Status" value={lead.status} />
          <Fact label="Business Name" value={lead.businessName} />
          <Fact label="Full Name" value={lead.fullName} />
          <Fact label="Business Relation/Title" value={lead.businessRelationTitle} />
          <Fact label="Last Modified" value={formatDateTime(lead.lastModifiedAt)} />
        </Card>
        <TextSection label="Proposal Summary" value={lead.proposalSummary} />
      </TabsContent>

      <TabsContent value="contact" className="space-y-3">
        <Card className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2">
          <Fact label="First Name" value={lead.firstName} />
          <Fact label="Last Name" value={lead.lastName} />
          <Fact label="Email Address" value={lead.email} linkKind="email" />
          <Fact label="Phone" value={lead.phone} linkKind="phone" />
          <Fact label="Business Phone" value={lead.businessPhone} linkKind="phone" />
          <Fact label="Business Website" value={lead.businessWebsite} linkKind="url" />
        </Card>
      </TabsContent>

      <TabsContent value="notes" className="space-y-3">
        <Card className="min-h-[280px] space-y-2 p-3">
          <DetailSectionHeading>Lead Notes</DetailSectionHeading>
          <MarkdownNotes notes={lead.leadNotes} />
        </Card>
      </TabsContent>

      <TabsContent value="relationships" className="space-y-3">
        <Card className="space-y-3 p-3">
          <Relationship label="Organization" values={lead.organizationIds} />
          <Relationship label="Contact Link" values={lead.contactLinkIds} />
          <Relationship label="BILL_Proposals" values={lead.billProposalIds} />
        </Card>
        <AttachmentSection label="Proposal Attachments" attachments={lead.proposalAttachments} />
        <AttachmentSection label="Business Logo" attachments={lead.businessLogo} />
        {lead.googleCalendarCreateMeetingUrl && (
          <Card className="p-3">
            <Button size="sm" variant="outline" asChild>
              <a href={lead.googleCalendarCreateMeetingUrl} target="_blank" rel="noreferrer">
                <CalendarPlus className="h-3.5 w-3.5" aria-hidden />
                Open meeting link
              </a>
            </Button>
          </Card>
        )}
      </TabsContent>
    </Tabs>
  );
}

interface LeadDraft {
  projectName: string;
  status: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  businessWebsite: string;
  leadNotes: string;
  businessRelationTitle: string;
  organizationIdsText: string;
  contactLinkIdsText: string;
  businessName: string;
  businessLogoText: string;
  businessPhone: string;
  proposalAttachmentsText: string;
  billProposalIdsText: string;
}

function LeadEditForm({
  draft,
  setDraft,
  nameRef,
}: {
  draft: LeadDraft;
  setDraft: React.Dispatch<React.SetStateAction<LeadDraft>>;
  nameRef?: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <FormField label="Project Name" className="sm:col-span-2">
        <Input
          ref={nameRef}
          value={draft.projectName}
          onChange={(event) => setDraft((d) => ({ ...d, projectName: event.target.value }))}
          required
        />
      </FormField>
      <FormField label="Status">
        <Select
          value={draft.status}
          onValueChange={(status) => setDraft((d) => ({ ...d, status }))}
        >
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LEAD_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
      <FormField label="Business Name">
        <Input
          value={draft.businessName}
          onChange={(event) => setDraft((d) => ({ ...d, businessName: event.target.value }))}
        />
      </FormField>
      <FormField label="First Name">
        <Input
          value={draft.firstName}
          onChange={(event) => setDraft((d) => ({ ...d, firstName: event.target.value }))}
        />
      </FormField>
      <FormField label="Last Name">
        <Input
          value={draft.lastName}
          onChange={(event) => setDraft((d) => ({ ...d, lastName: event.target.value }))}
        />
      </FormField>
      <FormField label="Email Address">
        <Input
          type="email"
          value={draft.email}
          onChange={(event) => setDraft((d) => ({ ...d, email: event.target.value }))}
        />
      </FormField>
      <FormField label="Phone">
        <Input
          type="tel"
          value={draft.phone}
          onChange={(event) => setDraft((d) => ({ ...d, phone: event.target.value }))}
        />
      </FormField>
      <FormField label="Business Phone">
        <Input
          type="tel"
          value={draft.businessPhone}
          onChange={(event) => setDraft((d) => ({ ...d, businessPhone: event.target.value }))}
        />
      </FormField>
      <FormField label="Business Website">
        <Input
          type="url"
          value={draft.businessWebsite}
          onChange={(event) => setDraft((d) => ({ ...d, businessWebsite: event.target.value }))}
        />
      </FormField>
      <FormField label="Business Relation/Title" className="sm:col-span-2">
        <Input
          value={draft.businessRelationTitle}
          onChange={(event) =>
            setDraft((d) => ({ ...d, businessRelationTitle: event.target.value }))
          }
        />
      </FormField>
      <FormField label="Lead Notes" className="sm:col-span-2">
        <TextArea
          value={draft.leadNotes}
          onChange={(leadNotes) => setDraft((d) => ({ ...d, leadNotes }))}
          rows={5}
        />
      </FormField>
      <FormField label="Organization Record IDs">
        <TextArea
          value={draft.organizationIdsText}
          onChange={(organizationIdsText) => setDraft((d) => ({ ...d, organizationIdsText }))}
          rows={2}
          placeholder="rec... one per line"
        />
      </FormField>
      <FormField label="Contact Link Record IDs">
        <TextArea
          value={draft.contactLinkIdsText}
          onChange={(contactLinkIdsText) => setDraft((d) => ({ ...d, contactLinkIdsText }))}
          rows={2}
          placeholder="rec... one per line"
        />
      </FormField>
      <FormField label="Business Logo URLs">
        <TextArea
          value={draft.businessLogoText}
          onChange={(businessLogoText) => setDraft((d) => ({ ...d, businessLogoText }))}
          rows={2}
          placeholder="https://..."
        />
      </FormField>
      <FormField label="Proposal Attachment URLs">
        <TextArea
          value={draft.proposalAttachmentsText}
          onChange={(proposalAttachmentsText) =>
            setDraft((d) => ({ ...d, proposalAttachmentsText }))
          }
          rows={2}
          placeholder="https://..."
        />
      </FormField>
      <FormField label="BILL_Proposals Record IDs" className="sm:col-span-2">
        <TextArea
          value={draft.billProposalIdsText}
          onChange={(billProposalIdsText) => setDraft((d) => ({ ...d, billProposalIdsText }))}
          rows={2}
          placeholder="rec... one per line"
        />
      </FormField>
    </div>
  );
}

function parseStatus(value: string | null): LeadStatusFilter | undefined {
  return LEAD_STATUSES.some((status) => status === value) ? (value as LeadStatusFilter) : undefined;
}

function matchesSearch(lead: Lead, query: string): boolean {
  return [
    lead.projectName,
    lead.businessName,
    lead.fullName,
    lead.email,
    lead.phone,
    lead.leadNotes,
  ].some((value) => value?.toLowerCase().includes(query));
}

function compareLeads(a: Lead, b: Lead): number {
  return (b.lastModifiedAt ?? b.createdAt ?? '').localeCompare(
    a.lastModifiedAt ?? a.createdAt ?? '',
  );
}

function computeStats(leads: Lead[]): Record<LeadStatusFilter, number> {
  const stats = Object.fromEntries(LEAD_STATUSES.map((status) => [status, 0])) as Record<
    LeadStatusFilter,
    number
  >;
  leads.forEach((lead) => {
    if (lead.status !== undefined && LEAD_STATUSES.some((status) => status === lead.status)) {
      stats[lead.status] = (stats[lead.status] ?? 0) + 1;
    }
  });
  return stats;
}

function renderStatus(status: string | undefined) {
  return status ? (
    <Badge variant={status === 'Signed!' || status === 'Converted' ? 'success' : 'muted'}>
      {status}
    </Badge>
  ) : (
    mutedDash()
  );
}

function ContactLinks({ lead, stacked = false }: { lead: Lead; stacked?: boolean }) {
  return (
    <div className={cn('flex flex-wrap gap-1.5', stacked && 'flex-col items-start')}>
      {lead.email ? (
        <a href={`mailto:${lead.email}`} className="text-primary hover:underline">
          {lead.email}
        </a>
      ) : (
        mutedDash()
      )}
      {lead.phone && (
        <a href={`tel:${normalizePhone(lead.phone)}`} className="text-primary hover:underline">
          {lead.phone}
        </a>
      )}
    </div>
  );
}

function ContactActionButtons({ lead }: { lead: Lead }) {
  return (
    <>
      {lead.email && (
        <Button size="sm" variant="outline" asChild>
          <a href={`mailto:${lead.email}`}>
            <Mail className="h-3.5 w-3.5" aria-hidden />
            Email Contact
          </a>
        </Button>
      )}
      {(lead.phone || lead.businessPhone) && (
        <Button size="sm" variant="outline" asChild>
          <a href={`tel:${normalizePhone(lead.phone ?? lead.businessPhone ?? '')}`}>
            <Phone className="h-3.5 w-3.5" aria-hidden />
            Call Contact
          </a>
        </Button>
      )}
    </>
  );
}

function WebsiteButton({ url }: { url: string | undefined }) {
  if (!url) return null;
  return (
    <Button size="sm" variant="outline" asChild>
      <a href={normalizeUrl(url)} target="_blank" rel="noreferrer">
        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        Open Website
      </a>
    </Button>
  );
}

function WebsiteLink({ url }: { url: string | undefined }) {
  if (!url) return mutedDash();
  return (
    <a
      href={normalizeUrl(url)}
      target="_blank"
      rel="noreferrer"
      className="text-primary hover:underline"
    >
      {url}
    </a>
  );
}

function ChipList({ values }: { values: string[] | undefined }) {
  if (!values?.length) return mutedDash();
  return (
    <div className="flex flex-wrap gap-1">
      {values.map((value) => (
        <Badge key={value} variant="outline">
          {value}
        </Badge>
      ))}
    </div>
  );
}

function Relationship({ label, values }: { label: string; values: string[] | undefined }) {
  return (
    <div>
      <DetailSectionHeading>{label}</DetailSectionHeading>
      <div className="mt-1">
        <ChipList values={values} />
      </div>
    </div>
  );
}

function AttachmentSection({
  label,
  attachments,
}: {
  label: string;
  attachments: ProposalAttachment[] | undefined;
}) {
  return (
    <Card className="space-y-2 p-3">
      <DetailSectionHeading>{label}</DetailSectionHeading>
      {attachments?.length ? (
        <div className="flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <a
              key={attachment.id ?? attachment.url}
              href={attachment.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2 py-1 text-[12px] font-medium text-primary hover:border-primary/40"
            >
              <FileText className="h-3.5 w-3.5" aria-hidden />
              {attachment.filename ?? 'Attachment'}
            </a>
          ))}
        </div>
      ) : (
        mutedDash()
      )}
    </Card>
  );
}

function TextSection({ label, value }: { label: string; value: string | undefined }) {
  if (!value) return null;
  return (
    <Card className="space-y-2 p-3">
      <DetailSectionHeading>{label}</DetailSectionHeading>
      <p className="whitespace-pre-wrap leading-relaxed text-foreground">{value}</p>
    </Card>
  );
}

function Fact({
  label,
  value,
  linkKind,
}: {
  label: string;
  value?: string;
  linkKind?: 'email' | 'phone' | 'url';
}) {
  let body: ReactNode = value || mutedDash();
  if (value && linkKind === 'email') {
    body = (
      <a href={`mailto:${value}`} className="text-primary hover:underline">
        {value}
      </a>
    );
  }
  if (value && linkKind === 'phone') {
    body = (
      <a href={`tel:${normalizePhone(value)}`} className="text-primary hover:underline">
        {value}
      </a>
    );
  }
  if (value && linkKind === 'url') {
    body = <WebsiteLink url={value} />;
  }

  return (
    <div>
      <DetailSectionHeading className="text-[10px]">{label}</DetailSectionHeading>
      <div className="mt-0.5 break-words text-[13px] font-medium text-foreground">{body}</div>
    </div>
  );
}

function FormField({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ''}`}>
      <span className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function TextArea({
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    />
  );
}

function MutationError({ error }: { error: unknown }) {
  return (
    <div className="mt-3 flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[12px] text-destructive">
      <AlertCircle className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden />
      <span>{error instanceof Error ? error.message : 'Request failed'}</span>
    </div>
  );
}

function emptyLeadDraft(): LeadDraft {
  return {
    projectName: '',
    status: DEFAULT_LEAD_STATUS,
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    businessWebsite: '',
    leadNotes: '',
    businessRelationTitle: '',
    organizationIdsText: '',
    contactLinkIdsText: '',
    businessName: '',
    businessLogoText: '',
    businessPhone: '',
    proposalAttachmentsText: '',
    billProposalIdsText: '',
  };
}

function toLeadDraft(lead: Lead): LeadDraft {
  return {
    projectName: lead.projectName,
    status: parseStatus(lead.status ?? null) ?? DEFAULT_LEAD_STATUS,
    firstName: lead.firstName ?? '',
    lastName: lead.lastName ?? '',
    phone: lead.phone ?? '',
    email: lead.email ?? '',
    businessWebsite: lead.businessWebsite ?? '',
    leadNotes: lead.leadNotes ?? '',
    businessRelationTitle: lead.businessRelationTitle ?? '',
    organizationIdsText: formatListText(lead.organizationIds),
    contactLinkIdsText: formatListText(lead.contactLinkIds),
    businessName: lead.businessName ?? '',
    businessLogoText: formatAttachmentText(lead.businessLogo),
    businessPhone: lead.businessPhone ?? '',
    proposalAttachmentsText: formatAttachmentText(lead.proposalAttachments),
    billProposalIdsText: formatListText(lead.billProposalIds),
  };
}

function toLeadInput(draft: LeadDraft): LeadInput {
  return {
    projectName: draft.projectName.trim(),
    status: draft.status,
    firstName: draft.firstName,
    lastName: draft.lastName,
    phone: draft.phone,
    email: draft.email,
    businessWebsite: draft.businessWebsite,
    leadNotes: draft.leadNotes,
    businessRelationTitle: draft.businessRelationTitle,
    organizationIds: parseListText(draft.organizationIdsText),
    contactLinkIds: parseListText(draft.contactLinkIdsText),
    businessName: draft.businessName,
    businessLogo: parseAttachmentText(draft.businessLogoText),
    businessPhone: draft.businessPhone,
    proposalAttachments: parseAttachmentText(draft.proposalAttachmentsText),
    billProposalIds: parseListText(draft.billProposalIdsText),
  };
}

function formatListText(values: string[] | undefined): string {
  return values?.join('\n') ?? '';
}

function parseListText(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatAttachmentText(values: ProposalAttachment[] | undefined): string {
  return values?.map((item) => item.url).join('\n') ?? '';
}

function parseAttachmentText(value: string): ProposalAttachment[] {
  return parseListText(value).map((url) => ({ url }));
}

function emptyDash(value: string | undefined) {
  return value || mutedDash();
}

function mutedDash() {
  return <span className="text-muted-foreground">-</span>;
}

function formatDateTime(value: string | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return date.toLocaleString();
}

function normalizeUrl(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function normalizePhone(value: string): string {
  return value.replace(/[^\d+]/g, '');
}
